import React, { useMemo, useState } from 'react';
import { PlayButton } from '@cp/ds/src/components/playButton';
import { ClientPhotoSize } from '@common/const/client/photo';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { ImageAssetItem } from '@common/model/resource/image';
import { ClientPhotoViewModel } from '@common/model/client/photo/view';
import { ResumeCard as BaseResumeCard } from '@cp/entities/resume';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { Image as Img } from '@cp/ui';
import { getResizedAssetItem, getResizedAssetItems } from '@cp/shared/image';
import { AudioPlayer } from '@cp/ds/src/components/audioPlayer';
import { styled } from '@cp/ds';
import { Stack } from '@cp/ds/src/components/stack';
import { useCatalogItemMedia } from '../../model';
import { CatalogItemResumeCardProps } from './model';
import { Achievement } from './achievement';
import { CARD_CONTAINER_SX } from './const';
import { Hidden } from './hidden';

export * from './const';
export * from './model';
export { UserMainData } from './userMainData';
export { Achievement } from './achievement';

const PlayButtonWrapper = styled(Stack)({
  position: 'absolute',
  left: '50%',
  bottom: '20px',
  transform: 'translateX(-50%)',
  zIndex: 2,
});

const mainPhotoToAsset = (photo: ClientPhotoViewModel, size: number): ImageAssetItem => {
  const resizedAsset = getResizedAssetItem(photo.image.base, size);
  return resizedAsset || { src: photo.notCroppedOriginalSrc as string, width: size, height: size };
};

export const ResumeCard: React.FC<CatalogItemResumeCardProps> = ({
  width = '100%',
  data,
  sx,
  onClick,
  favoriteButton,
  header,
  footer,
  component,
  loadPolicy = 'lazy',
  clientProfilePhotoPurpose = ClientPhotoPurpose.Avatar,
  showDescription,
}) => {
  const { media: catalogItemMedia, icon: CatalogItemMediaIcon } = useCatalogItemMedia(data);
  const [play, setPlay] = useState(false);
  const mergedSx = React.useMemo(() => mergeSx(CARD_CONTAINER_SX, sx), [sx]);

  const { mainAsset, assets } = useMemo(() => {
    const clientPhoto =
      clientProfilePhotoPurpose === ClientPhotoPurpose.Resume
        ? (data.clientProfile?.photoSet?.find((p) => p.purpose === clientProfilePhotoPurpose) ?? data.clientProfile?.mainPhoto)
        : data.clientProfile?.mainPhoto;
    if (!clientPhoto) {
      return { assets: [] as ReturnType<typeof getResizedAssetItems> };
    }
    return {
      assets: getResizedAssetItems(clientPhoto.image, ClientPhotoSize.Medium),
      mainAsset: mainPhotoToAsset(clientPhoto, ClientPhotoSize.Medium),
    };
  }, [clientProfilePhotoPurpose, data.clientProfile?.mainPhoto, data.clientProfile?.photoSet]);

  const handleMediaButtonClick = () => {
    setPlay(!play);
  };

  return (
    <BaseResumeCard
      achievements={
        <>
          {data.clientProfile?.trainingCompletedAt && <Achievement type="school" />}
          {data.clientProfile?.isOnline && <Achievement type="online" />}
          {data.clientProfile?.isDocumentsChecked && <Achievement type="verified" />}
        </>
      }
      component={component}
      data={data}
      data-itemid={data.id}
      footer={footer}
      header={header}
      hidden={<Hidden data={data} />}
      mediaButton={
        <PlayButtonWrapper>
          {catalogItemMedia && CatalogItemMediaIcon ? (
            play ? (
              <AudioPlayer
                autoPlay
                duration={catalogItemMedia.durationInMs}
                onClick={handleMediaButtonClick}
                size="small"
                source={catalogItemMedia.src}
              />
            ) : (
              <PlayButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleMediaButtonClick();
                }}
                size={3}
              >
                <CatalogItemMediaIcon />
              </PlayButton>
            )
          ) : null}
        </PlayButtonWrapper>
      }
      onClick={onClick}
      profilePhoto={
        <>
          {mainAsset && (
            <picture>
              {assets.map((asset) => (
                <source key={asset.src} srcSet={asset.src} type={asset.mimeType} />
              ))}
              <Img
                alt={[data.clientProfile?.name, data.clientProfile?.surname].filter(Boolean).join(' ')}
                height={mainAsset.height}
                loading={loadPolicy}
                src={mainAsset.src}
                sx={{ maxWidth: '100%', height: 'auto', display: 'block', margin: 'auto' }}
                width={mainAsset.width}
              />
            </picture>
          )}
          {favoriteButton}
        </>
      }
      showDescription={showDescription}
      sx={mergedSx}
      width={width}
    />
  );
};

export default ResumeCard;
